/**
 * The contact fields of the ONE details form (docs-company ADR-110
 * Amendment 6): name, email and phone, labelled, limited and checked the same
 * way whether the visitor is leaving their details (lead.ts, POST /chat/lead)
 * or giving them for a booking (actions.ts, `collect_details`). DetailsForm.tsx
 * draws both from here.
 *
 * Until Amendment 6 each form kept its own copy, and they drifted: the lead's
 * phone read "Phone (optional)" beside the booking's "Phone *", the lead took
 * an email of 200 characters and the booking one of 254, and a visitor who
 * had just filled in one was handed the other. Pure, so
 * scripts/test-config.mjs pins the rules against the built artefact.
 */

export const CONTACT_FIELDS = ['name', 'email', 'phone'] as const;

export type ContactField = (typeof CONTACT_FIELDS)[number];

/** Mirrors chat_lead's `_is_valid_email` and conversation_core's `_EMAIL_RE`. */
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The tighter of the two backends' limits, so either accepts what the form
 * lets through: chat_lead refuses an email over 200 characters where
 * conversation_core takes 254. A phone is 20 in both, with no format rule.
 */
export const CONTACT_LIMITS: Readonly<Record<ContactField, number>> = { name: 200, email: 200, phone: 20 };

export type ContactValues = Partial<Record<ContactField, string>>;

export type ContactValidation =
  | { ok: true; value: ContactValues }
  | { ok: false; field: ContactField; reason: 'required' | 'invalid' | 'tooLong' };

/**
 * Checked in the order the form shows the fields; the first problem wins.
 * Values are trimmed; an optional field left empty is left out, and a field
 * the form did not show is never read. An empty required email is reported
 * as not a valid address.
 */
export function validateContact(
  raw: ContactValues,
  fields: readonly ContactField[],
  required: readonly ContactField[],
): ContactValidation {
  const value: ContactValues = {};
  for (const field of fields) {
    const v = (raw[field] ?? '').trim();
    if (!v) {
      if (required.includes(field)) return { ok: false, field, reason: field === 'email' ? 'invalid' : 'required' };
      continue;
    }
    if (field === 'email' && !EMAIL_RE.test(v)) return { ok: false, field, reason: 'invalid' };
    if (v.length > CONTACT_LIMITS[field]) return { ok: false, field, reason: 'tooLong' };
    value[field] = v;
  }
  return { ok: true, value };
}
